import { useState } from "react";
import {
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { FaTrashAlt } from "react-icons/fa";
import AppForm from "components/AppForm/AppForm";
import CrudPage from "components/CrudPage/CrudPage";
import { withRemoteDataAndSpinner } from "hoc/withRemoteDataAndSpinner";
import withRouter from "hoc/withRouter";
import { useLoadAll } from "hooks/useLoadAll";
import { useGetStations } from "hooks/useGetStations";
import { useQuery } from "@tanstack/react-query";
import inputTypeEnum from "enums/inputTypes";
import { snack } from "components/Snack/Snack";
import baggageTariffsService from "services/admin/baggageTariffsService";
import counterpartiesService from "services/admin/counterpartiesService";

interface BaggageTariffFormProps {
  router: any;
  data: any;
}

type RouteRow = { stationAID: string; stationBID: string; ratePer10Kg: number | string };

// Редактор ставок по парам станций. Тариф симметричный — пара (А, Б) задаёт цену в обе стороны.
const RoutesEditor = ({
  routes,
  setRoutes,
  stationOptions,
}: {
  routes: RouteRow[];
  setRoutes: (r: RouteRow[]) => void;
  stationOptions: { id: string; name: string }[];
}) => {
  const updateRow = (index: number, patch: Partial<RouteRow>) => {
    setRoutes(routes.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };
  const removeRow = (index: number) => setRoutes(routes.filter((_, i) => i !== index));
  const addRow = () => setRoutes([...routes, { stationAID: "", stationBID: "", ratePer10Kg: 0 }]);

  return (
    <div className="m-b-16">
      <h3 className="m-b-16">Ставки по маршрутам (за 10 кг)</h3>
      <p style={{ color: "#666", marginBottom: 12 }}>
        Ставка действует в обе стороны (А ↔ Б). Маршрут без своей ставки считается по базовой ставке выше.
      </p>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Станция А</TableCell>
            <TableCell>Станция Б</TableCell>
            <TableCell style={{ width: 160 }}>Ставка/10кг (₸)</TableCell>
            <TableCell style={{ width: 56 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {routes.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} style={{ color: "#999" }}>
                Маршруты не заданы — используется базовая ставка тарифа.
              </TableCell>
            </TableRow>
          )}
          {routes.map((row, index) => (
            <TableRow key={index}>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={row.stationAID || ""}
                    displayEmpty
                    onChange={(e) => updateRow(index, { stationAID: e.target.value as string })}
                  >
                    <MenuItem value="">
                      <em>—</em>
                    </MenuItem>
                    {stationOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={row.stationBID || ""}
                    displayEmpty
                    onChange={(e) => updateRow(index, { stationBID: e.target.value as string })}
                  >
                    <MenuItem value="">
                      <em>—</em>
                    </MenuItem>
                    {stationOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <TextField
                  type="number"
                  size="small"
                  inputProps={{ min: 0 }}
                  value={row.ratePer10Kg}
                  onChange={(e) => updateRow(index, { ratePer10Kg: e.target.value })}
                />
              </TableCell>
              <TableCell>
                <IconButton color="error" size="small" onClick={() => removeRow(index)}>
                  <FaTrashAlt />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button variant="outlined" size="small" className="m-t-16" onClick={addRow}>
        + Добавить маршрут
      </Button>
    </div>
  );
};

const BaggageTariffForm = ({ router, data }: BaggageTariffFormProps) => {
  const {
    navigate,
    params: { id },
  } = router;
  const { tariff } = data;
  const orgID = localStorage.getItem("orgID") || "";

  const { data: stationsResponse } = useGetStations({ orgID });
  const stationOptions: { id: string; name: string }[] = ((stationsResponse as any)?.data || []).map(
    (s: any) => ({ id: s.id, name: s.name }),
  );

  // Контрагенты для договорного (корпоративного) тарифа. Пустой выбор = публичный/розничный тариф.
  const { data: counterpartiesResponse } = useQuery({
    queryKey: ["baggage-counterparties", { orgID, active: 1 }],
    queryFn: async () => {
      const res: any = await counterpartiesService.getAll({ active: 1 });
      return res.data;
    },
  });
  const counterpartyOptions = [
    { id: "", name: "Публичный (розничный)" },
    ...((counterpartiesResponse as any)?.data || [])
      .filter((c: any) => c.isActive)
      .map((c: any) => ({ id: c.id, name: c.bin ? `${c.name} (БИН ${c.bin})` : c.name })),
  ];

  const [routes, setRoutes] = useState<RouteRow[]>(
    (tariff?.routes || []).map((r: any) => ({
      stationAID: r.stationAID,
      stationBID: r.stationBID,
      ratePer10Kg: Number(r.ratePer10Kg),
    })),
  );

  const fieldsWithData = [
    {
      key: "name",
      label: "Название тарифа",
      required: true,
      inputType: inputTypeEnum.text,
    },
    {
      key: "counterpartyID",
      label: "Контрагент (юр. лицо) — пусто для публичного тарифа",
      inputType: inputTypeEnum.select,
      options: counterpartyOptions,
    },
    {
      key: "effectiveMonth",
      label: "Месяц действия (любая дата месяца)",
      inputType: inputTypeEnum.date,
    },
    {
      key: "ratePer10Kg",
      label: "Базовая ставка за 10 кг (₸, если маршрут не задан)",
      inputType: inputTypeEnum.number,
      options: { min: 0 },
    },
    {
      key: "declaredValueFee",
      label: "Сбор за объявленную ценность (₸)",
      inputType: inputTypeEnum.number,
      options: { min: 0 },
    },
    {
      key: "waybillFee",
      label: "Распечатка накладной (₸)",
      inputType: inputTypeEnum.number,
      options: { min: 0 },
    },
    {
      key: "currency",
      label: "Валюта",
      inputType: inputTypeEnum.text,
    },
    {
      key: "isActive",
      label: "Активный тариф",
      inputType: inputTypeEnum.checkbox,
    },
    {
      // Кастомная секция: редактор маршрутных ставок (AppForm рендерит label-функцию как JSX).
      key: "__routes",
      label: () => (
        <RoutesEditor routes={routes} setRoutes={setRoutes} stationOptions={stationOptions} />
      ),
    },
  ];

  // Дефолты при создании
  const formData = tariff || { currency: "KZT", isActive: true };

  const onSubmit = async (formValues: any) => {
    const cleanRoutes = routes
      .filter((r) => r.stationAID && r.stationBID && r.stationAID !== r.stationBID)
      .map((r) => ({
        stationAID: r.stationAID,
        stationBID: r.stationBID,
        ratePer10Kg: Number(r.ratePer10Kg) || 0,
      }));
    // Пустой выбор контрагента → публичный тариф (counterpartyID = null).
    const payload = {
      ...formValues,
      counterpartyID: formValues.counterpartyID ? Number(formValues.counterpartyID) : null,
      routes: cleanRoutes,
    };

    try {
      if (tariff) {
        await baggageTariffsService.put(id, payload);
        snack("Тариф обновлён успешно!");
      } else {
        await baggageTariffsService.post(payload);
        snack("Тариф создан успешно!");
      }

      setTimeout(() => {
        navigate(-1);
      }, 500);
    } catch (ex) {
      console.error(ex);
    }
  };

  return (
    <CrudPage title={tariff ? "Редактирование тарифа" : "Создание тарифа"}>
      <AppForm onSubmit={onSubmit} data={formData} fields={fieldsWithData}></AppForm>
    </CrudPage>
  );
};
export default withRouter(
  withRemoteDataAndSpinner(BaggageTariffForm, (router: any) => {
    let requests: any = {};
    if (router.params.id) {
      requests["tariff"] = () => baggageTariffsService.get(router.params.id);
    }
    return useLoadAll(requests);
  }),
);
